import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { createPortal } from 'react-dom';

import { Icon } from '@ds/core';
import { useCamada } from '@ds/feedback';

import '../estilo/voz.css';
import { OndaDaVoz } from './OndaDaVoz';
import { OrbeDaVecchia } from './OrbeDaVecchia';
import { SobreposicaoDaVoz } from './SobreposicaoDaVoz';
import type { AcoesDaVoz } from './useAcoesDaVoz';
import type { ConversaPorVoz, FaseDaVoz, MicrofoneDisponivel, VozAlternativa } from './useConversaPorVoz';

/**
 * `VoiceMode` (B9, mockup do usuário em 13/09/2026): a conversa por voz em tela cheia, clara,
 * com a VecchIA como orbe no centro, a frase de estado e a onda de barras embaixo dele, e três
 * controles redondos no rodapé.
 *
 * É só camada visual sobre a sessão que já existe (`useConversaPorVoz`, criada no `BrainApp`):
 * a sessão não pertence à tela, a tela consome a sessão. Abrir e fechar a decisão
 * (`SobreposicaoDaVoz`) não recria microfone, áudio, conversa nem o orbe — o contêiner do orbe
 * fica montado enquanto a tela está aberta, e só muda de lugar por `transform`.
 *
 *   VoiceMode
 *   ├── topo (fechar · VecchIA / Fórmula MED · opções)
 *   ├── OrbeDaVecchia
 *   ├── estado (frase · detalhe · OndaDaVoz)
 *   ├── SobreposicaoDaVoz (cartão de confirmação e desfecho)
 *   └── controles (escrever · microfone ou encerrar · mais)
 */

const TITULO_DA_FASE: Record<FaseDaVoz, string> = {
    idle: 'Pronta para ouvir você',
    listening: 'Ouvindo…',
    thinking: 'Pensando…',
    speaking: 'VecchIA está falando…',
    paused: 'Conversa pausada',
};

const DETALHE_DA_FASE: Record<FaseDaVoz, string> = {
    idle: 'Fale naturalmente',
    listening: '',
    thinking: '',
    speaking: '',
    paused: 'Toque no microfone para continuar',
};

/** O que o toque no microfone faz em cada fase (falando, o botão do meio encerra). */
const ACAO_DO_MICROFONE: Record<Exclude<FaseDaVoz, 'speaking'>, string> = {
    listening: 'Enviar agora',
    paused: 'Continuar a conversa',
    thinking: 'Aguarde a resposta',
    idle: 'Iniciando a conversa',
};

/**
 * O aviso de quem está falando — e, quando dá para saber, por quê.
 *
 * Antes dizia só "a voz Gacrux não respondeu", e quem ouvia concluía que a voz do Gemini
 * tinha quebrado. O motivo vem da borda e muda o que a pessoa faz a seguir: cota do dia
 * pede limite maior no painel do Google, cota do minuto passa sozinha, e chave faltando é
 * configuração.
 */
function avisoDaVozAlternativa(voz: VozAlternativa): string {
    if (voz.provedor === 'navegador') return 'Falando com a voz do navegador: a voz da VecchIA não respondeu.';
    if (voz.provedor !== 'openai') return `Falando com a voz de ${voz.provedor}.`;
    const volta = horaDaVolta(voz.ate);
    switch (voz.motivo) {
        case 'cota-do-dia':
            return `Voz reserva da OpenAI: a cota de hoje da voz Gacrux acabou${volta}.`;
        case 'cota':
            return `Voz reserva da OpenAI: a voz Gacrux atingiu o limite por minuto${volta}.`;
        case 'sem-chave':
            return 'Voz reserva da OpenAI: não há chave do Gemini ligada na plataforma.';
        default:
            return 'Voz reserva da OpenAI: a voz Gacrux não respondeu.';
    }
}

/** " — volta às 14:20" quando a borda disse quando tenta de novo, e hoje. */
function horaDaVolta(ate: string | null): string {
    if (!ate) return '';
    const quando = new Date(ate);
    if (Number.isNaN(quando.getTime()) || quando.getTime() <= Date.now()) return '';
    const hoje = new Date();
    const mesmoDia = quando.toDateString() === hoje.toDateString();
    const hora = quando.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    return mesmoDia ? ` — tentamos de novo às ${hora}` : ' — tentamos de novo amanhã';
}

/* Avisos do próprio microfone: aí a escolha do microfone aparece ali mesmo, sem abrir as opções. */
const AVISO_DE_MICROFONE = /microfone|ouvindo a sua voz/i;

export interface ModoDeVozProps {
    aberto: boolean;
    conversa: ConversaPorVoz;
    acoes: AcoesDaVoz;
    somenteLeitura?: string | null;
    aoFechar: () => void;
}

export function ModoDeVoz({ aberto, conversa, acoes, somenteLeitura, aoFechar }: ModoDeVozProps) {
    const camada = useCamada<HTMLDivElement>({ aberta: aberto, aoEsc: aoFechar, seletorDoPrimeiroFoco: '.brain-vozmodo__botao--principal' });
    const [opcoesAbertas, setOpcoesAbertas] = useState(false);
    const [escrevendo, setEscrevendo] = useState(false);
    const [texto, setTexto] = useState('');
    const campo = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (aberto) return;
        setOpcoesAbertas(false);
        setEscrevendo(false);
        setTexto('');
    }, [aberto]);

    useEffect(() => {
        if (escrevendo) campo.current?.focus();
    }, [escrevendo]);

    if (!aberto || typeof document === 'undefined') return null;

    const { fase, erro, aviso, transcricaoAoVivo, ultimaFala, modoManual, nivel, microfones, microfoneId, vozAlternativa } = conversa;
    const interacao = acoes.atual;
    const decidindo = Boolean(interacao?.visivel && interacao.estado === 'aguardando');
    const comCartao = Boolean(interacao?.visivel);
    const problemaDeMicrofone = Boolean(aviso) && AVISO_DE_MICROFONE.test(aviso);
    const falando = fase === 'speaking';

    let detalhe = DETALHE_DA_FASE[fase];
    let tom: 'normal' | 'aviso' | 'erro' = 'normal';
    if (erro) {
        detalhe = erro;
        tom = 'erro';
    } else if (aviso) {
        detalhe = aviso;
        tom = 'aviso';
    } else if (fase === 'listening' && transcricaoAoVivo) {
        detalhe = `“${transcricaoAoVivo}”`;
    } else if (fase === 'listening' && modoManual) {
        detalhe = 'Fale e toque no microfone quando terminar.';
    } else if (falando && ultimaFala) {
        detalhe = ultimaFala;
    }

    const enviar = (evento: FormEvent) => {
        evento.preventDefault();
        const limpo = texto.trim();
        if (!limpo) return;
        setTexto('');
        void conversa.enviarTexto(limpo);
    };

    const alternarOpcoes = () => setOpcoesAbertas((aberta) => !aberta);

    return createPortal(
        <div
            ref={camada}
            className={`brain-vozmodo brain-vozmodo--${fase}${comCartao ? ' brain-vozmodo--com-cartao' : ''}${decidindo ? ' brain-vozmodo--decidindo' : ''}`}
            role="dialog" aria-modal="true" aria-label="Conversa por voz" tabIndex={-1}
        >
            <header className="brain-vozmodo__topo">
                <button type="button" className="brain-vozmodo__redondo" onClick={aoFechar} aria-label="Encerrar conversa por voz">
                    <Icon name="x" size={18} />
                </button>
                <div className="brain-vozmodo__marca">
                    <span className="brain-vozmodo__nome"><span className="brain-vozmodo__simbolo" aria-hidden="true" />VecchIA</span>
                    <span className="brain-vozmodo__empresa">Fórmula MED</span>
                </div>
                <button type="button" className="brain-vozmodo__redondo" onClick={alternarOpcoes} aria-label="Opções da voz" aria-expanded={opcoesAbertas}>
                    <Icon name="settings" size={18} />
                </button>
                {opcoesAbertas && (
                    <div className="brain-vozmodo__opcoes" role="group" aria-label="Opções da voz">
                        {falando && (
                            <button type="button" className="brain-vozmodo__opcao" onClick={() => { setOpcoesAbertas(false); conversa.tocarNoMicrofone(); }}>
                                <Icon name="hand" size={16} />
                                Interromper e falar
                            </button>
                        )}
                        {microfones.length > 0 && <SeletorDeMicrofone microfones={microfones} microfoneId={microfoneId} aoEscolher={conversa.escolherMicrofone} />}
                        {vozAlternativa && <p className="brain-vozmodo__nota">{avisoDaVozAlternativa(vozAlternativa)}</p>}
                        <p className="brain-vozmodo__nota">Para encerrar, diga “obrigado, não preciso de mais nada”. Para interromper, é só falar por cima.</p>
                    </div>
                )}
            </header>

            <div className="brain-vozmodo__centro">
                <div className="brain-vozmodo__orbe">
                    <OrbeDaVecchia fase={fase} lerSinal={conversa.lerSinal} />
                </div>
                <div className="brain-vozmodo__estado" aria-live="polite">
                    <p className="brain-vozmodo__titulo">{TITULO_DA_FASE[fase]}</p>
                    {detalhe && <p className={`brain-vozmodo__detalhe brain-vozmodo__detalhe--${tom}`}>{detalhe}</p>}
                    {(fase === 'listening' || falando) && (
                        <OndaDaVoz fase={fase} lerEspectro={conversa.lerEspectro} nivel={nivel} medidor={fase === 'listening'} rotulo={TITULO_DA_FASE[fase]} />
                    )}
                </div>
            </div>

            <div className="brain-vozmodo__rodape">
                {problemaDeMicrofone && microfones.length > 0 && !opcoesAbertas && (
                    <SeletorDeMicrofone microfones={microfones} microfoneId={microfoneId} aoEscolher={conversa.escolherMicrofone} />
                )}
                {escrevendo && (
                    <form className="brain-vozmodo__texto" onSubmit={enviar}>
                        <input
                            ref={campo} value={texto} onChange={(e) => setTexto(e.target.value)}
                            aria-label="Mensagem para a VecchIA" placeholder="Escreva para a VecchIA" enterKeyHint="send" autoComplete="off"
                        />
                        <button type="submit" className="brain-vozmodo__redondo brain-vozmodo__redondo--enviar" aria-label="Enviar mensagem" disabled={!texto.trim()}>
                            <Icon name="arrow-up" size={18} />
                        </button>
                    </form>
                )}
                <div className="brain-vozmodo__controles" role="group" aria-label="Controles da voz">
                    <button
                        type="button" className="brain-vozmodo__redondo brain-vozmodo__redondo--grande" onClick={() => setEscrevendo((ativo) => !ativo)}
                        aria-label={escrevendo ? 'Fechar o campo de texto' : 'Escrever para a VecchIA'} aria-pressed={escrevendo}
                    >
                        <Icon name="keyboard" size={20} />
                    </button>
                    {falando ? (
                        <button type="button" className="brain-vozmodo__botao--principal brain-vozmodo__botao--encerrar" onClick={aoFechar} aria-label="Encerrar a conversa">
                            <Icon name="phone-off" size={24} />
                        </button>
                    ) : (
                        <button
                            type="button" className={`brain-vozmodo__botao--principal${fase === 'listening' ? ' brain-vozmodo__botao--ouvindo' : ''}`}
                            onClick={conversa.tocarNoMicrofone} aria-label={ACAO_DO_MICROFONE[fase]} disabled={fase === 'thinking' || fase === 'idle'}
                        >
                            <Icon name={fase === 'paused' ? 'play' : 'mic'} size={24} />
                        </button>
                    )}
                    {fase === 'listening' ? (
                        <button type="button" className="brain-vozmodo__redondo brain-vozmodo__redondo--grande" onClick={aoFechar} aria-label="Encerrar a conversa">
                            <Icon name="x" size={20} />
                        </button>
                    ) : (
                        <button
                            type="button" className="brain-vozmodo__redondo brain-vozmodo__redondo--grande" onClick={alternarOpcoes}
                            aria-label="Mais opções" aria-expanded={opcoesAbertas}
                        >
                            <Icon name={fase === 'idle' ? 'sliders-horizontal' : 'ellipsis'} size={20} />
                        </button>
                    )}
                </div>
            </div>

            <SobreposicaoDaVoz
                interacao={interacao} somenteLeitura={somenteLeitura ?? null}
                aoConfirmar={acoes.confirmar} aoCancelar={acoes.cancelar} aoDispensar={acoes.dispensar}
            />
        </div>,
        document.body,
    );
}

interface SeletorDeMicrofoneProps {
    microfones: MicrofoneDisponivel[];
    microfoneId: string;
    aoEscolher: (id: string) => void;
}

function SeletorDeMicrofone({ microfones, microfoneId, aoEscolher }: SeletorDeMicrofoneProps) {
    return (
        <label className="brain-vozmodo__microfone">
            <span>Microfone</span>
            <select value={microfoneId} onChange={(e) => aoEscolher(e.target.value)}>
                {microfones.map((m) => <option key={m.id || 'padrao'} value={m.id}>{m.nome}</option>)}
            </select>
        </label>
    );
}
